import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Briefcase, CalendarClock, AlertTriangle, ListTodo, Users, CheckCircle2,
  Euro, TrendingUp, Percent, Wallet, FileWarning, ShieldAlert, Activity, MapPin, Navigation, FileText,
} from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { isConsultant } from "@/lib/permissions";
import { fmtMoney, fmtDate, fmtDateShort } from "@/lib/format";
import { PageHeader, StatCard, SectionCard, Loading, EmptyState } from "@/components/common";
import { StatusBadge, PriorityBadge } from "@/components/StatusBadge";
import { JobCompletion } from "@/components/JobCompletion";
import { Btn } from "@/components/DataTable";

function CapacityBar({ jobs, max }) {
  const pct = Math.min(100, (jobs / (max || 4)) * 100);
  const tone = pct >= 100 ? "bg-red-500" : pct >= 75 ? "bg-amber-500" : "bg-seq-green";
  return (
    <div className="w-full h-2 bg-slate-100 rounded-full overflow-hidden">
      <div className={`h-full ${tone} transition-all`} style={{ width: `${pct}%` }} />
    </div>
  );
}

function ConsultantDashboard({ user }) {
  const [me, setMe] = useState(null);
  const [visits, setVisits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [completing, setCompleting] = useState(null);
  const today = new Date().toISOString().slice(0, 10);

  const load = async (consId) => {
    const id = consId || me?.id;
    if (!id) return;
    const { data } = await api.get(`/visits?consultant_id=${id}`);
    setVisits(data);
  };

  const reportPdf = async (vid) => {
    try {
      const res = await api.get(`/visits/${vid}/report.pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) { /* no-op */ }
  };

  useEffect(() => {
    (async () => {
      try {
        const { data: cons } = await api.get("/consultants");
        const mine = cons.find((c) => c.user_id === user.id) || cons.find((c) => c.email === user.email);
        setMe(mine);
        if (mine) { const { data } = await api.get(`/visits?consultant_id=${mine.id}`); setVisits(data); }
      } finally { setLoading(false); }
    })();
  }, [user]);

  if (loading) return <Loading />;
  const todays = visits.filter((v) => (v.scheduled_date || "").slice(0, 10) === today && !["Cancelled", "Unscheduled"].includes(v.status));
  const upcoming = visits.filter((v) => (v.scheduled_date || "").slice(0, 10) > today && !["Cancelled", "Unscheduled"].includes(v.status)).slice(0, 8);
  const next = todays[0] || upcoming[0];

  const JobCard = ({ v }) => (
    <div data-testid={`my-job-${v.id}`} className="border border-slate-200 rounded-xl p-4 bg-white">
      <div className="flex items-start justify-between gap-2">
        <div>
          <div className="font-medium text-slate-800">{v.job_title || v.service_name}</div>
          <div className="text-sm text-slate-500 flex items-center gap-1 mt-0.5"><MapPin size={13} /> {v.client_name} · {v.site_name}</div>
        </div>
        <StatusBadge status={v.status} />
      </div>
      <div className="flex items-center justify-between mt-3 text-sm">
        <span className="text-slate-600 font-medium">{fmtDate(v.scheduled_date)} · {v.start_time}–{v.end_time}</span>
        {v.gps_lat && (
          <a data-testid={`navigate-${v.id}`} target="_blank" rel="noreferrer"
            href={`https://www.google.com/maps/dir/?api=1&destination=${v.gps_lat},${v.gps_lng}`}
            className="inline-flex items-center gap-1 text-seq-green font-medium">
            <Navigation size={14} /> Navigate
          </a>
        )}
      </div>
      {v.site_address && <div className="text-xs text-slate-400 mt-2">{v.site_address}</div>}
      {!["Completed", "Closed", "Invoiced", "Cancelled", "Ready for Invoicing"].includes(v.status) ? (
        <Btn onClick={() => setCompleting(v.id)} className="w-full mt-3 py-1.5" data-testid={`open-job-${v.id}`}>
          {v.status === "In Progress" ? "Continue & complete" : "Start / complete job"}
        </Btn>
      ) : (
        <button onClick={() => reportPdf(v.id)} data-testid={`report-${v.id}`}
          className="w-full mt-3 py-1.5 text-sm rounded-lg border border-slate-300 text-slate-700 hover:bg-slate-50 flex items-center justify-center gap-1.5">
          <FileText size={14} /> Visit report PDF
        </button>
      )}
    </div>
  );

  return (
    <div>
      <PageHeader title={`Hi ${user.name.split(" ")[0]}`} subtitle="Your schedule for today" icon={Briefcase} />
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
        <StatCard label="Jobs Today" value={todays.length} icon={Briefcase} tone="green" testId="stat-jobs-today" />
        <StatCard label="Upcoming" value={upcoming.length} icon={CalendarClock} />
        <StatCard label="In Progress" value={visits.filter((v) => v.status === "In Progress").length} icon={Activity} tone="amber" />
        <StatCard label="Completed" value={visits.filter((v) => ["Completed", "Closed"].includes(v.status)).length} icon={CheckCircle2} />
      </div>
      {next && (
        <SectionCard title="Next appointment" className="mb-6">
          <JobCard v={next} />
        </SectionCard>
      )}
      <div className="grid lg:grid-cols-2 gap-5">
        <SectionCard title={`Today · ${todays.length} jobs`}>
          {todays.length ? <div className="space-y-3">{todays.map((v) => <JobCard key={v.id} v={v} />)}</div>
            : <EmptyState icon={CheckCircle2} title="No jobs scheduled today" hint="Enjoy the quiet — upcoming work is listed alongside." />}
        </SectionCard>
        <SectionCard title="Upcoming">
          {upcoming.length ? <div className="space-y-3">{upcoming.map((v) => <JobCard key={v.id} v={v} />)}</div>
            : <EmptyState icon={CalendarClock} title="Nothing upcoming" />}
        </SectionCard>
      </div>
      {completing && <JobCompletion visitId={completing} onClose={() => setCompleting(null)} onDone={() => { setCompleting(null); load(); }} />}
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [d, setD] = useState(null);

  useEffect(() => {
    if (isConsultant(user)) return;
    api.get("/dashboard").then((r) => setD(r.data)).catch(() => {});
  }, [user]);

  if (isConsultant(user)) return <ConsultantDashboard user={user} />;
  if (!d) return <Loading />;

  const role = d.role;
  const subtitle = {
    Administrator: "System overview, security and data quality",
    "Scheduler / Manager": "Today's operations and scheduling health",
    "Senior Management": "Performance, delivery and commercial overview",
    "Read Only": "Operational overview",
  }[role] || "Operational overview";

  return (
    <div>
      <PageHeader title={`Welcome, ${user.name.split(" ")[0]}`} subtitle={subtitle} icon={Activity} />

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-5">
        <StatCard label="Jobs Today" value={d.jobs_today} icon={Briefcase} tone="green" testId="stat-jobs-today" />
        <StatCard label="This Week" value={d.jobs_this_week} icon={CalendarClock} />
        <StatCard label="Unscheduled" value={d.unscheduled} icon={CalendarClock} tone={d.unscheduled ? "amber" : "default"} testId="stat-unscheduled" />
        <StatCard label="Overdue" value={d.overdue} icon={AlertTriangle} tone={d.overdue ? "red" : "default"} />
        <StatCard label="Actions Open" value={d.actions_open} icon={ListTodo} />
        <StatCard label="Available Today" value={`${d.consultants_available}/${d.consultants?.length || 0}`} icon={Users} tone="teal" />
      </div>

      {d.financial && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 mb-5" data-testid="financial-widgets">
          <StatCard label="Revenue (completed)" value={fmtMoney(d.revenue)} icon={Euro} tone="green" />
          <StatCard label="Gross Profit" value={fmtMoney(d.gross_profit)} icon={TrendingUp} tone="green" />
          <StatCard label="Gross Margin" value={`${d.gross_margin_pct}%`} icon={Percent} tone="teal" />
          <StatCard label="Future Work Value" value={fmtMoney(d.future_work_value)} icon={Wallet} />
          <StatCard label="Ready to Invoice" value={fmtMoney(d.ready_for_invoicing_value)} hint={`${d.ready_for_invoicing_count} visits`} icon={FileWarning} tone="amber" />
        </div>
      )}

      {(role === "Administrator") && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
          <StatCard label="Active Users" value={d.active_users} icon={Users} />
          <StatCard label="Failed Logins (24h)" value={d.failed_logins} icon={ShieldAlert} tone={d.failed_logins ? "red" : "default"} />
          <StatCard label="Audit Events Today" value={d.audit_today} icon={Activity} />
          <StatCard label="Expiring Quals (60d)" value={d.expiring_qualifications?.length || 0} icon={FileWarning} tone={(d.expiring_qualifications?.length) ? "amber" : "default"} />
        </div>
      )}

      <div className="grid lg:grid-cols-3 gap-5">
        <SectionCard title="Upcoming this week" className="lg:col-span-2" noPad
          action={<button onClick={() => nav("/calendar")} className="text-xs text-seq-green font-medium">Open calendar</button>}>
          {d.upcoming_visits?.length ? (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wider">
                  <tr><th className="text-left font-medium px-4 py-2">Job</th><th className="text-left font-medium px-4 py-2">Client</th><th className="text-left font-medium px-4 py-2">Consultant</th><th className="text-left font-medium px-4 py-2">Date</th><th className="text-left font-medium px-4 py-2">Status</th></tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {d.upcoming_visits.slice(0, 10).map((v) => (
                    <tr key={v.id} className="hover:bg-slate-50">
                      <td className="px-4 py-2 font-medium text-slate-700">{v.job_title}</td>
                      <td className="px-4 py-2 text-slate-500">{v.client_name}</td>
                      <td className="px-4 py-2 text-slate-500">{v.consultant_name}</td>
                      <td className="px-4 py-2 text-slate-500">{fmtDateShort(v.scheduled_date)} {v.start_time}</td>
                      <td className="px-4 py-2"><StatusBadge status={v.status} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : <EmptyState icon={CheckCircle2} title="No visits scheduled this week" />}
        </SectionCard>

        <SectionCard title="Consultant capacity today">
          <div className="space-y-3">
            {d.consultants?.map((c) => (
              <div key={c.id}>
                <div className="flex items-center justify-between text-sm mb-1">
                  <span className="flex items-center gap-2 text-slate-700">
                    <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: c.colour }} />
                    {c.name}
                  </span>
                  <span className={`text-xs ${c.on_leave ? "text-red-500" : "text-slate-400"}`}>
                    {c.on_leave ? "On leave" : `${c.jobs_today}/${c.max_jobs} jobs`}
                  </span>
                </div>
                {c.on_leave ? <div className="w-full h-2 bg-red-100 rounded-full" /> : <CapacityBar jobs={c.jobs_today} max={c.max_jobs} />}
              </div>
            ))}
          </div>
        </SectionCard>
      </div>

      <div className="grid lg:grid-cols-2 gap-5 mt-5">
        <SectionCard title="Upcoming leave & availability" noPad>
          {d.leave_upcoming?.length ? (
            <div className="divide-y divide-slate-100">
              {d.leave_upcoming.slice(0, 6).map((l) => (
                <div key={l.id} className="flex items-center justify-between px-4 py-2.5 text-sm">
                  <span className="text-slate-700">{l.consultant_name} · <span className="text-slate-500">{l.leave_type}</span></span>
                  <span className="text-slate-400 text-xs">{fmtDateShort(l.start_date)} – {fmtDateShort(l.end_date)}</span>
                </div>
              ))}
            </div>
          ) : <EmptyState icon={Users} title="No upcoming leave" />}
        </SectionCard>

        {role === "Administrator" && d.expiring_qualifications?.length > 0 && (
          <SectionCard title="Expiring qualifications" noPad>
            <div className="divide-y divide-slate-100">
              {d.expiring_qualifications.map((q, i) => (
                <div key={i} className="flex items-center justify-between px-4 py-2.5 text-sm">
                  <span className="text-slate-700">{q.consultant} · <span className="text-slate-500">{q.cert}</span></span>
                  <span className="text-amber-600 text-xs font-medium">{fmtDate(q.expiry)}</span>
                </div>
              ))}
            </div>
          </SectionCard>
        )}
      </div>
    </div>
  );
}
